import { motion } from "framer-motion";
import { useSite } from "@/contexts/SiteContent";
import { Icon } from "@/components/Icon";
import PhotoFrame from "@/components/PhotoFrame";
import RichText from "@/components/RichText";

export default function AboutPage() {
  const { content } = useSite();
  const a = content?.about_page || {};
  const hero = a.hero || {};

  return (
    <div>
      {/* ===== HERO — image LEFT, eyebrow + title + subtitle RIGHT ===== */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 pb-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {hero.image_url && (
            <motion.div
              initial={{opacity:0, x:-24}} animate={{opacity:1, x:0}} transition={{duration:0.6}}
              className="lg:col-span-5 flex justify-center lg:justify-start">
              <div className="w-full max-w-[460px]">
                <PhotoFrame src={hero.image_url} alt={hero.title || "About TARA Finserv"} ratio="4/5"/>
              </div>
            </motion.div>
          )}
          <motion.div
            initial={{opacity:0, x:24}} animate={{opacity:1, x:0}} transition={{duration:0.6, delay:0.1}}
            className={hero.image_url ? "lg:col-span-7" : "lg:col-span-12"}>
            <span className="eyebrow-bar"><span className="overline">{hero.eyebrow || "About us"}</span></span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-4 text-navy">{hero.title}</h1>
            <RichText html={hero.subtitle} variant="subtitle" className="mt-6" testid="about-hero-subtitle"/>
          </motion.div>
        </div>
      </section>

      {/* ===== VISION & MISSION — stand-alone two-column band ===== */}
      {(a.vision_text || a.mission_text) && (
        <section className="bg-white border-y border-slate-200">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-2 gap-12">
            {a.vision_text && (
              <motion.div initial={{opacity:0, y:14}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{duration:0.5}}>
                <span className="eyebrow-bar"><span className="overline">{a.vision_title || "Vision"}</span></span>
                <RichText html={a.vision_text} variant="lead" className="mt-4 font-display font-bold text-navy"/>
              </motion.div>
            )}
            {a.mission_text && (
              <motion.div initial={{opacity:0, y:14}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{duration:0.5, delay:0.1}}>
                <span className="eyebrow-bar"><span className="overline">{a.mission_title || "Mission"}</span></span>
                <RichText html={a.mission_text} variant="body" className="mt-4"/>
              </motion.div>
            )}
          </div>
        </section>
      )}

      {(a.leadership || []).length > 0 && (
        <section className="bg-slate-50">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
            <span className="eyebrow-bar"><span className="overline">Leadership</span></span>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold mt-3 text-navy">{a.leadership_heading}</h2>
            <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-7">
              {a.leadership.map((t, i) => (
                <motion.div key={t.id || t.name} initial={{opacity:0, y:16}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{duration:0.5, delay: i*0.08}}
                  data-testid={`team-${(t.initials || t.name?.slice(0,2)).toLowerCase()}`}>
                  {t.photo_url ? (
                    <PhotoFrame src={t.photo_url} alt={t.name} ratio="1/1"/>
                  ) : (
                    <div className="photo-frame">
                      <div className="photo-frame__inner aspect-square grid place-items-center bg-navy text-saffron font-display font-extrabold text-4xl">{t.initials}</div>
                    </div>
                  )}
                  <div className="mt-6">
                    <div className="font-display font-bold text-lg text-navy">{t.name}</div>
                    <div className="text-sm text-ink-muted">{t.role}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {(a.values || []).length > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
          <span className="eyebrow-bar"><span className="overline">Core Values</span></span>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold mt-3 max-w-2xl text-navy">{a.values_heading}</h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {a.values.map((v, i) => (
              <motion.div key={i} initial={{opacity:0, y:14}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{duration:0.4, delay: i*0.06}} className="card-flat p-7">
                <div className="h-12 w-12 grid place-items-center rounded-2xl bg-navy text-saffron"><Icon name={v.icon} className="h-5 w-5" strokeWidth={1.8}/></div>
                <div className="font-display text-xl font-bold mt-5 text-navy">{v.title}</div>
                <div className="text-sm text-ink-muted mt-2 leading-relaxed">{v.desc}</div>
              </motion.div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
